import type {Shortcut} from './professions';

type DragPayload={binding:Shortcut;source?:number};
type HotbarApi={slots:()=>Shortcut[];bind:(slot:number,binding:Shortcut,source?:number)=>void;notice:(text:string)=>void;open:()=>void;online?:()=>boolean};
const MIME='application/phi-shortcut';
const SLOT='[data-hud-skill],[data-slot],[data-touch-slot]';
function slotOf(node:HTMLElement):number{return Number(node.dataset.hudSkill??node.dataset.slot??node.dataset.touchSlot);}
function payload(raw:string):DragPayload|null{
 try{const v=JSON.parse(raw) as DragPayload;if(!v||!v.binding||!['skill','item'].includes(v.binding.kind)||typeof v.binding.id!=='string'||v.binding.id.length>80)return null;return v;}catch{return null;}
}

/** UI-only editing preferences; entitlement and atomic mutation stay on the server. */
export class HotbarDrag {
 private abort=new AbortController();
 private locked=true;
 private picked:DragPayload|null=null;
 private touch:{id:number;x:number;y:number;value:DragPayload;moved:boolean}|null=null;
 private ghost:HTMLElement|null=null;
 private suppressUntil=0;
 constructor(private api:HotbarApi){
  try{this.locked=localStorage.getItem('phi-hotbar-locked')!=='0';}catch{/* Safe default. */}
  const options={capture:true,signal:this.abort.signal};
  document.addEventListener('click',this.click,options);
  document.addEventListener('dragstart',this.start,options);
  document.addEventListener('dragover',this.over,options);
  document.addEventListener('drop',this.drop,options);
  document.addEventListener('dragend',()=>this.clear(),options);
  document.addEventListener('pointerdown',this.down,options);
  document.addEventListener('pointermove',this.move,{...options,passive:false});
  document.addEventListener('pointerup',this.up,options);
  document.addEventListener('pointercancel',()=>this.clear(),options);
  // Phaser also listens to native touch events; HUD touches must not hit world NPCs.
  for(const type of ['touchstart','touchmove','touchend'] as const)document.addEventListener(type,e=>{
   if((e.target as Element).closest(SLOT+',[data-pick],.hotbar-toolbar,.shortcut-toolbar'))e.stopImmediatePropagation();
  },options);
  this.sync();
 }
 get dragging():boolean{return !!this.touch?.moved||!!this.ghost;}
 sync():void{
  document.body.classList.toggle('hotbar-editing',!this.locked);
  for(const b of document.querySelectorAll<HTMLElement>('[data-hotbar-lock]')){
   b.textContent=this.locked?'🔒 ล็อกช่อง':'🔓 จัดช่อง';b.setAttribute('aria-pressed',String(this.locked));b.setAttribute('aria-label',this.locked?'ปลดล็อกเพื่อจัดช่องลัด':'ล็อกช่องลัดเพื่อเข้าสู่การต่อสู้');
  }
  for(const n of document.querySelectorAll<HTMLElement>(SLOT+',[data-pick]'))n.draggable=!this.locked&&!!this.read(n)?.binding;
 }
 private read(node:HTMLElement):DragPayload|null{
  if(node.matches(SLOT)){const source=slotOf(node);return {binding:this.api.slots()[source]??null,source};}
  const binding=payload(JSON.stringify({binding:JSON.parse(node.dataset.pick??'null')}))?.binding;
  if(!binding)return null;
  const source=this.api.slots().findIndex(b=>b?.kind===binding.kind&&b.id===binding.id);
  return {binding,...(source>=0?{source}:{})};
 }
 private click=(e:MouseEvent):void=>{
  const n=(e.target as Element).closest<HTMLElement>('[data-hotbar-lock],[data-hotbar-open],[data-hotbar-remove],'+SLOT+',[data-pick]');if(!n)return;
  if(Date.now()<this.suppressUntil){e.preventDefault();e.stopImmediatePropagation();return;}
  if(n.hasAttribute('data-hotbar-lock')){e.preventDefault();e.stopImmediatePropagation();this.locked=!this.locked;this.picked=null;this.clear();try{localStorage.setItem('phi-hotbar-locked',this.locked?'1':'0');}catch{}this.sync();this.api.notice(this.locked?'ล็อกช่องแล้ว · พร้อมต่อสู้':'จัดช่อง: ลากหรือแตะต้นทางแล้วแตะปลายทาง');return;}
  if(n.hasAttribute('data-hotbar-open')){e.preventDefault();e.stopImmediatePropagation();this.api.open();return;}
  if(this.locked){if(n.matches('[data-slot],[data-pick]')){e.stopImmediatePropagation();this.api.notice('ปลดล็อกแม่กุญแจก่อนจัดช่อง');}return;}
  e.preventDefault();e.stopImmediatePropagation();
  if(n.hasAttribute('data-hotbar-remove')){if(this.picked?.source!==undefined)this.api.bind(this.picked.source,null);this.picked=null;return;}
  if(n.dataset.pick==='null'){this.picked={binding:null};this.api.notice('เลือกช่องที่ต้องการถอด');return;}
  if(n.matches(SLOT)&&this.picked){this.apply(n,this.picked);this.picked=null;return;}
  this.picked=this.read(n);this.api.notice(this.picked?.binding?'เลือกแล้ว · แตะช่องปลายทาง หรือ “ถอด”':'เลือกสกิลหรือไอเทมจากคลัง');
 };
 private start=(e:DragEvent):void=>{
  const n=(e.target as Element).closest<HTMLElement>(SLOT+',[data-pick]');if(!n)return;
  if(this.locked){e.preventDefault();return;}const value=this.read(n);if(!value?.binding){e.preventDefault();return;}
  e.dataTransfer?.setData(MIME,JSON.stringify(value));if(e.dataTransfer)e.dataTransfer.effectAllowed='move';this.picked=value;
 };
 private over=(e:DragEvent):void=>{
  if(this.locked||!e.dataTransfer?.types.includes(MIME))return;
  const n=(e.target as Element).closest<HTMLElement>(SLOT+',[data-hotbar-remove]');if(n){e.preventDefault();this.highlight(n);if(e.dataTransfer)e.dataTransfer.dropEffect='move';}
 };
 private drop=(e:DragEvent):void=>{
  const n=(e.target as Element).closest<HTMLElement>(SLOT+',[data-hotbar-remove]');if(!n||this.locked)return;
  const value=payload(e.dataTransfer?.getData(MIME)??'');if(!value)return;e.preventDefault();e.stopImmediatePropagation();this.apply(n,value);this.clear();this.picked=null;this.suppressUntil=Date.now()+350;
 };
 private down=(e:PointerEvent):void=>{
  const n=(e.target as Element).closest<HTMLElement>(SLOT+',[data-pick]');if(!n||this.locked)return;
  // Editing never casts a skill through the combat pointerdown adapter.
  e.stopImmediatePropagation();if(e.pointerType==='mouse')return;
  // Library text remains scrollable; its icon is the explicit touch drag handle.
  if(n.hasAttribute('data-pick')&&!(e.target as Element).closest('.shortcut-icon'))return;
  const value=this.read(n);if(!value?.binding)return;
  this.touch={id:e.pointerId,x:e.clientX,y:e.clientY,value,moved:false};n.setPointerCapture(e.pointerId);
 };
 private move=(e:PointerEvent):void=>{
  const d=this.touch;if(!d||d.id!==e.pointerId||this.locked)return;
  if(Math.hypot(e.clientX-d.x,e.clientY-d.y)>10)d.moved=true;if(!d.moved)return;e.preventDefault();
  if(!this.ghost){this.ghost=document.createElement('div');this.ghost.className='hotbar-drag-ghost';this.ghost.textContent='◈ '+d.value.binding!.id;document.body.append(this.ghost);}
  this.ghost.style.left=e.clientX+'px';this.ghost.style.top=e.clientY+'px';this.highlight(document.elementFromPoint(e.clientX,e.clientY)?.closest<HTMLElement>(SLOT+',[data-hotbar-remove]')??null);
 };
 private up=(e:PointerEvent):void=>{
  const d=this.touch;if(!d||d.id!==e.pointerId)return;
  if(d.moved){const n=document.elementFromPoint(e.clientX,e.clientY)?.closest<HTMLElement>(SLOT+',[data-hotbar-remove]');if(n&&!this.locked)this.apply(n,d.value);this.suppressUntil=Date.now()+350;e.preventDefault();e.stopImmediatePropagation();this.picked=null;}this.clear();
 };
 private apply(n:HTMLElement,value:DragPayload):void{
  if(n.hasAttribute('data-hotbar-remove')){if(value.source!==undefined)this.api.bind(value.source,null);}
  else this.api.bind(slotOf(n),value.binding,value.source);
  this.api.notice(this.api.online?.()?'ส่งคำขอจัดช่อง · รอผลยืนยันจากเซิร์ฟเวอร์':'จัดช่องแล้ว · ล็อกแม่กุญแจก่อนต่อสู้');
 }
 private highlight(node:HTMLElement|null):void{document.querySelectorAll('.drop-hover').forEach(n=>n.classList.remove('drop-hover'));node?.classList.add('drop-hover');}
 private clear():void{this.touch=null;this.ghost?.remove();this.ghost=null;this.highlight(null);}
 cancel():void{this.picked=null;this.clear();}
 destroy():void{this.abort.abort();this.cancel();}
}
